<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h2 class="text-xl font-semibold mb-2">Data Quality</h2>
      <p class="text-gray-600">Affärsmöjligheter med avslutsdatum som har passerat</p>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">Försenade affärsmöjligheter</p>
            <p class="text-3xl font-semibold mt-2">{{ formatNumber(totalOverdue) }}</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #FEE2E2">
            <BaseIcon icon="exclamation-triangle" class="text-lg" style="color: #DC2626" />
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">Totalt värde (försenat)</p>
            <p class="text-3xl font-semibold mt-2">{{ formatNumber(totalOverdueValue) }} kr</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #FEF3C7">
            <BaseIcon icon="exclamation-triangle" class="text-lg" style="color: #D97706" />
          </div>
        </div>
      </div>
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-600">Påverkade säljare</p>
            <p class="text-3xl font-semibold mt-2">{{ formatNumber(overdueOpportunities.length) }}</p>
          </div>
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background-color: #DBEAFE">
            <BaseIcon icon="exclamation-triangle" class="text-lg" style="color: #1E40AF" />
          </div>
        </div>
      </div>
    </div>

    <!-- Toggle All -->
    <div v-if="overdueOpportunities.length > 0" class="flex justify-end">
      <button class="px-3 py-1 border border-gray-300 rounded-md text-sm" @click="toggleAll">
        {{ expandedSellers.length === overdueOpportunities.length ? 'Stäng alla' : 'Expandera alla' }}
      </button>
    </div>

    <!-- Seller List -->
    <div v-if="overdueOpportunities.length > 0" class="space-y-4">
      <div v-for="sellerItem in overdueOpportunities" :key="sellerItem.seller" class="bg-white rounded-lg shadow overflow-hidden">
        <!-- Seller Header -->
        <button class="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left" @click="toggleSeller(sellerItem.seller)">
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <BaseIcon icon="exclamation-triangle" class="text-red-600" />
            </div>
            <div>
              <h3 class="font-semibold text-lg">{{ sellerItem.seller }}</h3>
              <p class="text-sm text-gray-600">
                {{ formatNumber(sellerItem.count) }} försenade affärsmöjligheter
                <span v-if="sellerItem.oldestDate" class="ml-2">
                  &bull; Äldsta: {{ new Date(sellerItem.oldestDate).toLocaleDateString('sv-SE') }}
                  ({{ getDaysOverdue(sellerItem.oldestDate) }} dagar sedan)
                </span>
              </p>
            </div>
          </div>
          <div class="flex items-center gap-4">
            <div class="text-right">
              <p class="font-semibold">{{ formatNumber(sellerItem.totalValue) }} kr</p>
              <p class="text-sm text-gray-600">Totalt värde</p>
            </div>
            <BaseIcon :icon="isExpanded(sellerItem.seller) ? 'chevron-up' : 'chevron-down'" class="text-gray-400" />
          </div>
        </button>

        <!-- Opportunities Table -->
        <div v-if="isExpanded(sellerItem.seller)" class="border-t">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left p-3 border-b whitespace-nowrap">#</th>
                  <th class="text-left p-3 border-b whitespace-nowrap">Företag</th>
                  <th class="text-left p-3 border-b whitespace-nowrap">Avslutsdatum</th>
                  <th class="text-right p-3 border-b whitespace-nowrap">Dagar sedan</th>
                  <th class="text-right p-3 border-b whitespace-nowrap">Ordervärde</th>
                  <th class="text-left p-3 border-b whitespace-nowrap">Fas</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in sellerItem.opportunities" :key="index" class="hover:bg-gray-50 border-b">
                  <td class="p-3 whitespace-nowrap">{{ index + 1 }}</td>
                  <td class="p-3">{{ row['Företag'] || row['Order: Företag'] || row['col_3'] || '-' }}</td>
                  <td class="p-3 whitespace-nowrap">{{ row['Avslutsdatum'] ? new Date(row['Avslutsdatum']).toLocaleDateString('sv-SE') : '-' }}</td>
                  <td class="p-3 text-right whitespace-nowrap">
                    <span class="inline-flex px-2 py-1 rounded text-sm font-medium"
                      :class="getDaysOverdue(row['Avslutsdatum']) > 90 ? 'bg-red-100 text-red-800' : getDaysOverdue(row['Avslutsdatum']) > 30 ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'">
                      {{ getDaysOverdue(row['Avslutsdatum']) }} dagar
                    </span>
                  </td>
                  <td class="p-3 text-right whitespace-nowrap">{{ formatNumber(Number(row['Ordervärde']) || 0) }} kr</td>
                  <td class="p-3 whitespace-nowrap">{{ row['Fas'] || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- No overdue -->
    <div v-else class="bg-white rounded-lg shadow p-12">
      <div class="text-center text-gray-500">
        <BaseIcon icon="check-circle" class="text-green-500 text-5xl mb-4" />
        <h3 class="text-xl font-semibold mb-2">Inga försenade affärsmöjligheter!</h3>
        <p>Alla affärsmöjligheter har avslutsdatum framåt i tiden.</p>
      </div>
    </div>

    <!-- Star Performers -->
    <div v-if="sellersWithoutOverdue.length > 0" class="rounded-lg p-6 border-2 border-yellow-300" style="background: linear-gradient(to bottom right, #FFFBEB, #FFF7ED)">
      <div class="flex items-center gap-3 mb-4">
        <div class="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center">
          <BaseIcon icon="star" class="text-yellow-900 text-lg" />
        </div>
        <div>
          <h3 class="text-xl font-semibold text-yellow-900">Stjärnpresterande säljare</h3>
          <p class="text-sm text-yellow-800">Inga försenade affärsmöjligheter!</p>
        </div>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <div v-for="seller in sellersWithoutOverdue" :key="seller" class="flex items-center gap-2 p-3 bg-white rounded-lg shadow-sm border border-yellow-200">
          <BaseIcon icon="star" class="text-yellow-500" />
          <span class="font-medium text-gray-900">{{ seller }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PipelineDataQuality",
  props: {
    data: { type: Array, required: true },
  },
  data() {
    return {
      expandedSellers: [],
    };
  },
  computed: {
    overdueOpportunities() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const overdue = this.data.filter((row) => {
        const dateStr = row["Avslutsdatum"];
        if (!dateStr) return false;
        const closeDate = new Date(dateStr);
        closeDate.setHours(0, 0, 0, 0);
        return closeDate < today;
      });

      const sellerMap = {};
      overdue.forEach((row) => {
        const seller = row["Säljare"] || "Okänd";
        if (!sellerMap[seller]) sellerMap[seller] = [];
        sellerMap[seller].push(row);
      });

      return Object.entries(sellerMap)
        .map(([seller, opportunities]) => {
          const sorted = opportunities.sort((a, b) => {
            const dateA = a["Avslutsdatum"] ? new Date(a["Avslutsdatum"]).getTime() : 0;
            const dateB = b["Avslutsdatum"] ? new Date(b["Avslutsdatum"]).getTime() : 0;
            return dateA - dateB;
          });
          const totalValue = opportunities.reduce((sum, opp) => sum + (Number(opp["Ordervärde"]) || 0), 0);
          const oldestDate = sorted[0]["Avslutsdatum"] ? new Date(sorted[0]["Avslutsdatum"]).toISOString() : null;
          return { seller, count: opportunities.length, opportunities: sorted, totalValue, oldestDate };
        })
        .sort((a, b) => b.count - a.count);
    },
    totalOverdue() {
      return this.overdueOpportunities.reduce((sum, s) => sum + s.count, 0);
    },
    totalOverdueValue() {
      return this.overdueOpportunities.reduce((sum, s) => sum + s.totalValue, 0);
    },
    sellersWithoutOverdue() {
      const allSellers = new Set(this.data.map((row) => row["Säljare"]).filter((s) => s && s !== "Okänd"));
      const sellersWithOverdue = new Set(this.overdueOpportunities.map((s) => s.seller).filter((s) => s !== "Okänd"));
      return Array.from(allSellers).filter((seller) => !sellersWithOverdue.has(seller)).sort();
    },
  },
  methods: {
    formatNumber(num) {
      return num.toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    getDaysOverdue(dateStr) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const closeDate = new Date(dateStr);
      closeDate.setHours(0, 0, 0, 0);
      return Math.ceil((today.getTime() - closeDate.getTime()) / (1000 * 60 * 60 * 24));
    },
    isExpanded(seller) {
      return this.expandedSellers.includes(seller);
    },
    toggleSeller(seller) {
      const index = this.expandedSellers.indexOf(seller);
      if (index > -1) {
        this.expandedSellers.splice(index, 1);
      } else {
        this.expandedSellers.push(seller);
      }
    },
    toggleAll() {
      if (this.expandedSellers.length === this.overdueOpportunities.length) {
        this.expandedSellers = [];
      } else {
        this.expandedSellers = this.overdueOpportunities.map((s) => s.seller);
      }
    },
  },
};
</script>
